'use client';

import type * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SidebarTrigger } from '@/components/ui/sidebar';
import { cn } from '@/lib/utils';

export interface Tab {
  id: string;
  title: string;
  icon: React.ComponentType<{ className?: string }>;
}

interface TabHeaderProps {
  tabs: Tab[];
  activeTabId: string | null;
  onTabClick: (tabId: string) => void;
  onTabClose: (tabId: string) => void;
  /** 우측 고정 영역 (알림 벨 등 전 탭 공통 액션) */
  trailing?: React.ReactNode;
}

/** 버튼을 누르고 있는 동안 프레임당 움직일 픽셀 */
const HOLD_SCROLL_STEP = 8;

export function TabHeader({
  tabs,
  activeTabId,
  onTabClick,
  onTabClose,
  trailing,
}: TabHeaderProps) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const holdRef = useRef(0);
  const [isOverflowing, setIsOverflowing] = useState(false);

  // 탭 추가·삭제와 창 크기 변경 모두에서 넘침 여부가 바뀐다.
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;

    const update = () => setIsOverflowing(el.scrollWidth > el.clientWidth);
    update();

    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [tabs]);

  // Shift+휠을 가로 스크롤로 바꾼다. 브라우저 기본 동작에 맡기면 플랫폼마다 달라 실제로
  // 움직이지 않는 조합이 있었다. React 의 onWheel 은 passive 로 붙어 preventDefault 가
  // 무시되므로 직접 등록한다. 브라우저가 이미 변환해 deltaX 를 준 경우엔 두 번 움직이지 않도록 둔다.
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;

    const onWheel = (event: WheelEvent) => {
      if (!event.shiftKey || event.deltaX !== 0) return;
      el.scrollLeft += event.deltaY;
      event.preventDefault();
    };

    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  // pointerdown 부터 pointerup/leave 까지 계속 스크롤한다. 한 번 클릭하면 첫 프레임만 실행돼
  // 한 칸 이동하는 효과가 된다.
  const startHoldScroll = (direction: 1 | -1) => {
    const el = scrollerRef.current;
    if (!el) return;

    const step = () => {
      el.scrollLeft += direction * HOLD_SCROLL_STEP;
      holdRef.current = requestAnimationFrame(step);
    };
    step();
  };

  const stopHoldScroll = () => cancelAnimationFrame(holdRef.current);

  useEffect(() => stopHoldScroll, []);

  const scrollButtonProps = (direction: 1 | -1) => ({
    variant: 'ghost' as const,
    size: 'sm' as const,
    className: 'h-8 w-6 p-0 rounded-none flex-shrink-0',
    onPointerDown: () => startHoldScroll(direction),
    onPointerUp: stopHoldScroll,
    onPointerLeave: stopHoldScroll,
    onPointerCancel: stopHoldScroll,
  });

  return (
    <div className='flex items-center bg-background border-b border-border'>
      {/* 모바일에서는 사이드바가 오프캔버스(Sheet)로 닫혀 있어 여는 버튼이 필요하다 */}
      <SidebarTrigger className='ml-1 flex-shrink-0 md:hidden' />
      {/* flex-1 min-w-0 이라야 남은 폭만 차지하고 넘치는 만큼 스크롤된다. */}
      <div
        ref={scrollerRef}
        className='flex flex-1 min-w-0 items-center overflow-x-auto scrollbar-hide'
      >
        {tabs.map(tab => (
          <div
            key={tab.id}
            className={cn(
              'group flex flex-shrink-0 items-center gap-2 px-3 py-2 border-r border-border bg-muted/30 hover:bg-muted/50 transition-colors cursor-pointer min-w-0 max-w-[200px]',
              activeTabId === tab.id &&
                'bg-background border-b-2 border-b-primary'
            )}
            onClick={() => onTabClick(tab.id)}
          >
            <tab.icon className='size-4 flex-shrink-0' />
            <span className='text-sm truncate flex-1 min-w-0'>{tab.title}</span>
            <Button
              variant='ghost'
              size='sm'
              className='h-4 w-4 p-0 opacity-0 group-hover:opacity-100 hover:bg-destructive/20 hover:text-destructive transition-opacity'
              onClick={e => {
                e.stopPropagation();
                onTabClose(tab.id);
              }}
            >
              <X className='size-3' />
            </Button>
          </div>
        ))}
      </div>
      {isOverflowing && (
        <div className='flex flex-shrink-0 items-center border-l border-border'>
          <Button {...scrollButtonProps(-1)} title='왼쪽으로 이동'>
            <ChevronLeft className='size-4' />
          </Button>
          <Button {...scrollButtonProps(1)} title='오른쪽으로 이동'>
            <ChevronRight className='size-4' />
          </Button>
        </div>
      )}
      {trailing && (
        <div className='flex flex-shrink-0 items-center border-l border-border px-1'>{trailing}</div>
      )}
    </div>
  );
}
